import { StyledButton, StyledFieldSet, StyledForm, StyledFormField } from '@/components/styles';
import { Checkbox, FormField, SelectDirections, SelectSpecialists } from '@/components';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm, FormContext } from 'react-hook-form';
import { regExp, legalLinks } from '@/lib/config';
import { usePromise } from '@/hooks';
import { useModal } from './Modal';
import axios from 'axios';
import api from '@/lib/api';
import { useTranslation } from 'react-i18next';

export default function AppointmentForm() {
  const formMethods = useForm();
  const { register, handleSubmit, setValue, reset } = formMethods;
  const { t } = useTranslation('global');

  const [activeDirection, setActiveDirection] = useState({});
  const [activeSpecialistId, setActiveSpecialistId] = useState(null);

  const cancelTokenSource = useRef(axios.CancelToken.source());
  const appointmentPromise = usePromise(
    () => api.appointment({ direction: activeDirection.slug, cancelToken: cancelTokenSource.token }),
    [activeDirection],
    {},
    () => cancelTokenSource.current.cancel()
  );

  const { specialists = [], directions = [] } = appointmentPromise.result;

  const [status, setStatus] = useState({
    success: false,
    loading: false,
    error: false,
  });
  const errorInput = useRef();

  useEffect(() => {
    register({ name: 'direction' });
    setValue('direction', '');
    register({ name: 'specialist' });
    setValue('specialist', '');
  }, []);

  const handleError = () => {
    setStatus((prevStatus) => ({ ...prevStatus, loading: false, error: true }));
    errorInput.current.focus();
  };

  const onFormSubmit = async (data) => {
    console.log(data);
    setStatus((prevStatus) => ({ ...prevStatus, loading: true }));

    try {
      const resp = await api.appointmentCreate(data);
      if (resp.result === 'success') {
        reset();
        setStatus((prevStatus) => ({ ...prevStatus, loading: false, success: true }));
      } else {
        handleError();
      }
    } catch (error) {
      handleError();
    }
  };

  const handleDirectionChange = useCallback(
    (value) => {
      const direction = directions.reduce((acc, current) => {
        if (current.slug === value) return current;
        if (current.children?.length > 0) {
          const found = current.children.find((dir) => dir.slug === value);
          if (found) return found;
        }
        return acc;
      }, {});

      setActiveDirection(direction);
      setValue('direction', direction.id || '');
      // reset spacialist on direction change
      setActiveSpecialistId(null);
      setValue('specialist', '');
    },
    [directions]
  );

  const handleSpecialistChange = useCallback(
    (value) => {
      setActiveSpecialistId(value);
      const specialist = specialists.find((spec) => spec.id === value);
      setValue('specialist', specialist.id);
    },
    [specialists]
  );

  return status.success ? (
    <Success />
  ) : (
    <FormContext {...formMethods}>
      <StyledForm as="form" action="/" onSubmit={handleSubmit(onFormSubmit)} modal autoComplete="off">
        <StyledForm.Title>{t('appointmentForm.title')}</StyledForm.Title>
        <StyledForm.Desc>{t('appointmentForm.desc')}</StyledForm.Desc>

        {status.error && <StyledForm.Error ref={errorInput} value="Ошибка сервера, попробуйте позже" readOnly />}

        <StyledForm.Block>
          <StyledFieldSet>
            <StyledFieldSet.Legend>{t('appointmentForm.personalTitle')}</StyledFieldSet.Legend>
            <StyledForm.Row>
              <StyledForm.Item>
                <FormField
                  label={t('appointmentForm.name')}
                  name="first_name"
                  validateRules={{
                    required: true,
                    validate: (value) => {
                      if (value.length && value.trim().length) {
                        return true;
                      } else {
                        return false;
                      }
                    },
                  }}
                  autoFocus
                />
              </StyledForm.Item>

              <StyledForm.Item>
                <FormField
                  label={t('appointmentForm.lastName')}
                  name="last_name"
                  validateRules={{
                    required: true,
                    validate: (value) => {
                      if (value.length && value.trim().length) {
                        return true;
                      } else {
                        return false;
                      }
                    },
                  }}
                />
              </StyledForm.Item>
            </StyledForm.Row>

            <StyledForm.Row>
              <StyledForm.Item>
                <FormField
                  label={t('appointmentForm.phone')}
                  name="phone"
                  type="tel"
                  inputmode="tel"
                  placeholder=""
                  validateRules={{ required: true, pattern: regExp.phoneSimple }}
                />
              </StyledForm.Item>

              <StyledForm.Item>
                <FormField
                  label="E-mail"
                  name="email"
                  type="text"
                  inputmode="email"
                  validateRules={{
                    validate: (value) => {
                      if (value.length < 1) {
                        return true;
                      } else {
                        return regExp.email.test(value);
                      }
                    },
                  }}
                />
              </StyledForm.Item>
            </StyledForm.Row>
          </StyledFieldSet>
        </StyledForm.Block>

        <StyledForm.Block styledLast>
          <StyledFieldSet>
            <StyledFieldSet.Legend>{t('appointmentForm.serviceTitle')}</StyledFieldSet.Legend>

            {(directions.length > 0 || specialists.length > 0) && (
              <StyledForm.Row>
                <StyledForm.Item>
                  <StyledFormField noDecor>
                    <StyledFormField.Title>{t('appointmentForm.direction')}</StyledFormField.Title>

                    <SelectDirections
                      includeAllItemsVariant
                      directions={directions}
                      active={activeDirection}
                      onChange={handleDirectionChange}
                      placeholder={t('appointmentForm.chooseDirection')}
                      selectType="form"
                      selected={!!activeDirection?.slug}
                    />
                  </StyledFormField>
                </StyledForm.Item>

                <StyledForm.Item>
                  <StyledFormField noDecor>
                    <StyledFormField.Title>{t('appointmentForm.specialist')}</StyledFormField.Title>

                    <SelectSpecialists
                      specialists={specialists}
                      activeId={(specialists.find((spec) => spec.id === activeSpecialistId) || {}).id}
                      onChange={handleSpecialistChange}
                      placeholder={t('appointmentForm.chooseSpecialist')}
                      selectType="form"
                      selected={!!activeSpecialistId}
                      disabled={specialists.length === 0}
                    />
                  </StyledFormField>
                </StyledForm.Item>
              </StyledForm.Row>
            )}

            <StyledForm.Row>
              <StyledForm.Item>
                <FormField inputAs="textarea" label={t('appointmentForm.comment')} name="comment" />
              </StyledForm.Item>
            </StyledForm.Row>

            <StyledForm.Row>
              <StyledForm.Item>
                <Checkbox name="emailing" defaultChecked>
                  {t('appointmentForm.emailing.text')}{' '}
                  <a href={legalLinks.newsletter} target="_blank" rel="noopener noreferrer">
                    {t('appointmentForm.emailing.link')}
                  </a>
                  .
                </Checkbox>
              </StyledForm.Item>
            </StyledForm.Row>
          </StyledFieldSet>
        </StyledForm.Block>

        <StyledForm.Submit styledWide>
          <StyledButton type="submit" disabled={status.loading} isLoading={status.loading}>
            {t('appointmentForm.submit')}
            <StyledButton.Loader />
          </StyledButton>
        </StyledForm.Submit>

        {/* <StyledForm.Footer>
          {t('appointmentForm.legal.text')}{' '}
          <a href={legalLinks.publicOffer} target="_blank" rel="noopener noreferrer">
            {t('appointmentForm.legal.link')}
          </a>
          {'.'}
        </StyledForm.Footer> */}
      </StyledForm>
    </FormContext>
  );
}

function Success() {
  const modal = useModal();
  const { closeModal } = modal;
  const { t } = useTranslation('global');

  return (
    <StyledForm as="div" modal>
      <StyledForm.Title centered>{t('appointmentForm.thanks.title')}</StyledForm.Title>
      <StyledForm.Desc centered>{t('appointmentForm.thanks.desc')}</StyledForm.Desc>
      <StyledForm.Submit>
        <StyledButton type="button" onClick={closeModal}>
          {t('appointmentForm.thanks.button')}
        </StyledButton>
      </StyledForm.Submit>
    </StyledForm>
  );
}
